import type { UserProfile, WarehouseDataset } from "../types";

const DB_NAME = "cangxu-intelligence";
const DB_VERSION = 1;
const DATASET_KEY = "warehouse-dataset";
const PROFILE_KEY = "cangxu.profile.v2";
const BUSINESS_RESET_KEY = "cangxu.business-reset.v1";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("state")) db.createObjectStore("state");
      if (!db.objectStoreNames.contains("media")) db.createObjectStore("media");
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveDataset(dataset: WarehouseDataset) {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("state", "readwrite");
    tx.objectStore("state").put(dataset, DATASET_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadDataset() {
  const db = await openDb();
  return new Promise<WarehouseDataset | null>((resolve, reject) => {
    const request = db.transaction("state", "readonly").objectStore("state").get(DATASET_KEY);
    request.onsuccess = () => resolve((request.result as WarehouseDataset | undefined) ?? null);
    request.onerror = () => reject(request.error);
  });
}

export async function clearDataset() {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("state", "readwrite");
    tx.objectStore("state").delete(DATASET_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function resetLegacyBusinessData() {
  if (localStorage.getItem(BUSINESS_RESET_KEY) === "done") return false;
  await clearDataset();
  localStorage.setItem(BUSINESS_RESET_KEY, "done");
  return true;
}

export async function saveMediaBlob(key: string, blob: Blob) {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("media", "readwrite");
    tx.objectStore("media").put(blob, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadMediaBlob(key: string) {
  const db = await openDb();
  return new Promise<Blob | null>((resolve, reject) => {
    const request = db.transaction("media", "readonly").objectStore("media").get(key);
    request.onsuccess = () => resolve((request.result as Blob | undefined) ?? null);
    request.onerror = () => reject(request.error);
  });
}

export function loadProfile(): UserProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? (JSON.parse(raw) as UserProfile) : null;
  } catch {
    return null;
  }
}

export function saveProfile(profile: UserProfile) {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}
